import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CampoDeSelecao, Cartao, EstadoCarregando } from '../../componentes/ui';
import {
  CHAVE_DOS_QUADROS,
  carregarQuadro,
  carregarQuadros,
  chaveDoQuadro,
} from '../../servicos/crm';
import { ErroDaApi } from '../ErroDaApi';
import { SecaoDeTags, SecaoDoQuadro } from './ConfiguracoesDoPipeline';

/**
 * As configurações do CRM na Central de Configurações — as mesmas da
 * engrenagem do Pipeline (que continua lá), com o quadro escolhido num
 * seletor em vez do quadro aberto.
 */
export function ConfiguracoesDoCrm(): JSX.Element {
  const quadros = useQuery({ queryKey: CHAVE_DOS_QUADROS, queryFn: carregarQuadros });
  const [quadroId, setQuadroId] = useState('');
  const lista = quadros.data ?? [];

  // Abre no quadro padrão (o primeiro); se o escolhido some, volta para ele.
  useEffect(() => {
    if (lista.length > 0 && !lista.some((q) => q.id === quadroId)) setQuadroId(lista[0]!.id);
  }, [lista, quadroId]);

  const quadro = useQuery({
    queryKey: chaveDoQuadro(quadroId),
    queryFn: () => carregarQuadro(quadroId),
    enabled: quadroId !== '',
  });
  const dados = quadro.data;
  const usos = useMemo(() => {
    const contagem = new Map<string, number>();
    for (const card of dados?.colunas.flatMap((c) => c.cards) ?? []) {
      for (const tagId of card.tagIds) contagem.set(tagId, (contagem.get(tagId) ?? 0) + 1);
    }
    return contagem;
  }, [dados]);

  if (quadros.isError) {
    return <ErroDaApi titulo="Não foi possível carregar os quadros do CRM" erro={quadros.error} />;
  }
  if (quadros.data === undefined) {
    return <EstadoCarregando variante="cartao" contexto="os quadros do CRM" />;
  }

  return (
    <Cartao como="section" titulo="Pipeline" subtitulo="O mesmo da engrenagem do Pipeline.">
      <div className="flex flex-col gap-8">
        <CampoDeSelecao
          rotulo="Quadro"
          className="sm:max-w-xs"
          value={quadroId}
          onChange={(e) => setQuadroId(e.target.value)}
          opcoes={lista.map((q) => ({ valor: q.id, rotulo: `${q.nome} (${q.cards})` }))}
        />
        {quadro.isError ? (
          <ErroDaApi titulo="Não foi possível carregar o quadro" erro={quadro.error} />
        ) : dados === undefined ? (
          <EstadoCarregando variante="cartao" contexto="o quadro" />
        ) : (
          <>
            <SecaoDoQuadro
              quadro={{
                id: dados.id,
                nome: dados.nome,
                cards: dados.colunas.reduce((soma, c) => soma + c.cards.length, 0),
              }}
              unicoQuadro={lista.length === 1}
              aoExcluirQuadro={() => setQuadroId('')}
              descricao="O quadro escolhido acima. As etapas se mexem no próprio quadro, no Pipeline, pelo menu de cada coluna."
            />
            <SecaoDeTags tags={dados.tags} usos={usos} contagemDe="neste quadro" />
          </>
        )}
      </div>
    </Cartao>
  );
}
